import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import EmailPreviewModal from '../components/EmailPreviewModal';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://healthcare.visionkraftconsulting.com/api'
  : 'http://localhost:3025/api';

const EmailPreviewPage = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [useCustomEmail, setUseCustomEmail] = useState(false);
  const [ccEmails, setCcEmails] = useState('');
  const [bccEmails, setBccEmails] = useState('');
  const [customToEmails, setCustomToEmails] = useState({});
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [dropdownPreview, setDropdownPreview] = useState({});
  const [loadingPreview, setLoadingPreview] = useState({});

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/billing/invoice/records`);
      setInvoices(response.data.invoices || []);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
      setError('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoices(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedInvoices(invoices.map(invoice => invoice.id));
    } else {
      setSelectedInvoices([]);
    }
  };

  const initializeEmailTemplate = () => {
    if (selectedInvoices.length === 0) return;
    
    const selectedInvoiceData = invoices.filter(inv => selectedInvoices.includes(inv.id));
    const clientName = selectedInvoiceData[0]?.bill_to_name || 'Customer';
    
    let invoiceNumbers = selectedInvoiceData.map(inv => 
      inv.invoice_number || inv.filename?.replace('.pdf', '') || `HUHC${inv.id}`
    );
    
    const invoiceList = invoiceNumbers.length > 1 
      ? `invoices ${invoiceNumbers.join(', ')}`
      : `invoice ${invoiceNumbers[0]}`;
    
    const subjectInvoiceList = invoiceNumbers.length > 1 
      ? `Invoices ${invoiceNumbers.join(', ')}`
      : `Invoice ${invoiceNumbers[0]}`;
    setEmailSubject(`${subjectInvoiceList} from Hearts United Healthcare`);
    setEmailTemplate(`Dear ${clientName},

Thank you for your continued trust in our services. Please find the attached ${invoiceList} for your review.

${selectedInvoiceData.map(inv => {
  const invoiceNum = inv.invoice_number || inv.filename?.replace('.pdf', '') || `HUHC${inv.id}`;
  return `• Invoice ${invoiceNum}`;
}).join('\n')}

Please remit payment by the due date indicated on the invoice${invoiceNumbers.length > 1 ? 's' : ''}.

Thank you for your business.

Best regards,
Hearts United Healthcare Team`);
  };

  const handleProceedToEmail = () => {
    if (selectedInvoices.length === 0) {
      setError('Please select at least one invoice to send');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    initializeEmailTemplate();
    setShowEmailForm(true);
  };

  const handleSendEmails = async () => {
    if (selectedInvoices.length === 0) {
      setError('Please select at least one invoice to send');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setSending(true);
    setError('');
    setSuccessMessage('');

    try {
      // Parse CC and BCC emails
      const ccEmailArray = ccEmails ? ccEmails.split(',').map(email => email.trim()).filter(email => email) : [];
      const bccEmailArray = bccEmails ? bccEmails.split(',').map(email => email.trim()).filter(email => email) : [];
      
      const emailData = {
        invoiceIds: selectedInvoices,
        subject: emailSubject,
        template: useCustomEmail ? emailTemplate : null,
        useCustomTemplate: useCustomEmail,
        ccEmails: ccEmailArray,
        bccEmails: bccEmailArray,
        customToEmails: customToEmails
      };

      const response = await axios.post(`${API_BASE_URL}/billing/invoice/send-emails`, emailData);
      
      if (response.data.success) {
        setSuccessMessage(`Successfully sent ${selectedInvoices.length} email(s)!`);
        setSelectedInvoices([]);
        setShowEmailForm(false);
        setUseCustomEmail(false);
        // Refresh invoice list to show updated status
        fetchInvoices();
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        throw new Error(response.data.message || 'Failed to send emails');
      }
    } catch (err) {
      console.error('Failed to send emails:', err);
      setError(err.response?.data?.message || 'Failed to send emails. Please try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSending(false);
    }
  };



  const handleCloseEmailPreview = () => {
    setShowEmailPreview(false);
    setSelectedInvoiceId(null);
  };

  const handleToggleDropdownPreview = async (invoiceId) => {
    if (dropdownPreview[invoiceId]) {
      // Close dropdown if already open
      setDropdownPreview(prev => ({
        ...prev,
        [invoiceId]: null
      }));
      return;
    }

    // Load email preview
    setLoadingPreview(prev => ({ ...prev, [invoiceId]: true }));
    
    try {
      const response = await axios.get(`${API_BASE_URL}/billing/invoice/email-preview/${invoiceId}`);
      
      if (response.data.success) {
        setDropdownPreview(prev => ({
          ...prev,
          [invoiceId]: response.data.email
        }));
      } else {
        console.error('Failed to load email preview');
      }
    } catch (err) {
      console.error('Error fetching email preview:', err);
    } finally {
      setLoadingPreview(prev => ({ ...prev, [invoiceId]: false }));
    }
  };

  // Clear dropdown previews when navigating away
  useEffect(() => {
    return () => {
      setDropdownPreview({});
    };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return <div className="container"><p>Loading invoices...</p></div>;
  }

  if (showEmailForm) {
    return (
      <div className="container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Send Invoice Emails</h2>
          <button 
            className="btn btn-outline-secondary"
            onClick={() => setShowEmailForm(false)}
          >
            Back to Selection
          </button>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {successMessage && <div className="alert alert-success">{successMessage}</div>}

        <div className="row">
          <div className="col-md-8">
            <div className="card">
              <div className="card-header">
                <h5>Email Details</h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Subject</label>
                  <input
                    type="text"
                    className="form-control"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">CC Emails (comma separated)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={ccEmails}
                    onChange={(e) => setCcEmails(e.target.value)}
                    placeholder="email1@example.com, email2@example.com"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">BCC Emails (comma separated)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={bccEmails}
                    onChange={(e) => setBccEmails(e.target.value)}
                    placeholder="email1@example.com, email2@example.com"
                  />
                </div>

                <div className="mb-3 form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="useCustomEmail"
                    checked={useCustomEmail}
                    onChange={(e) => setUseCustomEmail(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="useCustomEmail">
                    Use custom email template
                  </label>
                </div>

                {useCustomEmail && (
                  <div className="mb-3">
                    <label className="form-label">Email Template</label>
                    <textarea
                      className="form-control"
                      rows="12"
                      value={emailTemplate}
                      onChange={(e) => setEmailTemplate(e.target.value)}
                      placeholder="Enter your custom email message here..."
                    />
                  </div>
                )}

                {!useCustomEmail && (
                  <div className="mb-3">
                    <label className="form-label">Email Preview (Default Template)</label>
                    <div className="p-3 bg-light border rounded">
                      <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                        {emailTemplate}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card">
              <div className="card-header">
                <h5>Selected Invoices ({selectedInvoices.length})</h5>
              </div>
              <div className="card-body">
                {invoices.filter(inv => selectedInvoices.includes(inv.id)).map((invoice) => (
                  <div key={invoice.id} className="mb-3 p-3 border rounded">
                    <div className="fw-bold mb-2">
                      {invoice.invoice_number || invoice.filename?.replace('.pdf', '') || `HUHC${invoice.id}`}
                    </div>
                    <div className="text-muted small mb-2">
                      {invoice.bill_to_name}
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">TO Email:</label>
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        value={customToEmails[invoice.id] || invoice.bill_to_email || ''}
                        onChange={(e) => setCustomToEmails({
                          ...customToEmails,
                          [invoice.id]: e.target.value
                        })}
                        placeholder="Enter email address"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <button
            className="btn btn-primary me-2"
            onClick={handleSendEmails}
            disabled={sending}
          >
            {sending ? 'Sending...' : `Send ${selectedInvoices.length} Email(s)`}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => setShowEmailForm(false)}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Send Invoice Emails</h2>
        <button 
          className="btn btn-outline-secondary"
          onClick={() => navigate('/invoice-list')}
        >
          Back to Invoice List
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          {selectedInvoices.length > 0 && (
            <span className="badge bg-primary me-2">
              {selectedInvoices.length} selected
            </span>
          )}
          <small className="text-muted">
            Select invoices to send via email
          </small>
        </div>
        <button
          className="btn btn-success"
          onClick={handleProceedToEmail}
          disabled={selectedInvoices.length === 0}
        >
          <i className="bi bi-envelope me-2"></i>
          Proceed to Email ({selectedInvoices.length})
        </button>
      </div>

      {invoices.length === 0 ? (
        <div className="alert alert-info">
          No invoices found. <button className="btn btn-link p-0" onClick={() => navigate('/invoice')}>Generate your first invoice</button>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={selectedInvoices.length === invoices.length && invoices.length > 0}
                  />
                </th>
                <th>Invoice #</th>
                <th>Date</th>
                <th>Client</th>
                <th>Email</th>
                <th>Due Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <React.Fragment key={invoice.id}>
                  <tr>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedInvoices.includes(invoice.id)}
                        onChange={() => handleSelectInvoice(invoice.id)}
                      />
                    </td>
                    <td>{invoice.invoice_number || invoice.filename?.replace('.pdf', '') || `HUHC${invoice.id}`}</td>
                    <td>{formatDate(invoice.created_at)}</td>
                    <td>{invoice.bill_to_name}</td>
                    <td>{invoice.bill_to_email}</td>
                    <td>{invoice.due_date}</td>
                    <td>
                      {invoice.email_sent ? (
                        <div>
                          <span className="badge bg-success me-2">Email Sent</span>
                          <button
                            onClick={() => handleToggleDropdownPreview(invoice.id)}
                            className={`btn btn-sm ${dropdownPreview[invoice.id] ? 'btn-info' : 'btn-outline-info'}`}
                            title="Toggle Email Preview"
                            disabled={loadingPreview[invoice.id]}
                          >
                            {loadingPreview[invoice.id] ? (
                              <span className="spinner-border spinner-border-sm" role="status"></span>
                            ) : (
                              <i className={`bi ${dropdownPreview[invoice.id] ? 'bi-chevron-up' : 'bi-envelope-open'}`}></i>
                            )}
                          </button>
                        </div>
                      ) : (
                        <span className="badge bg-warning">Pending</span>
                      )}
                    </td>
                  </tr>
                  {/* Dropdown Email Preview */}
                  {dropdownPreview[invoice.id] && (
                    <tr>
                      <td colSpan="6" className="p-0">
                        <div className="card border-0 border-top">
                          <div className="card-body bg-light">
                            <div className="row">
                              <div className="col-md-8">
                                <h6 className="card-title mb-3">
                                  <i className="bi bi-envelope me-2"></i>
                                  Email Preview
                                </h6>
                                
                                <div className="mb-3">
                                  <strong>Subject:</strong> {dropdownPreview[invoice.id].subject}
                                </div>
                                
                                <div className="mb-3">
                                  <strong>Message:</strong>
                                  <div 
                                    className="border rounded p-3 bg-white mt-2"
                                    style={{ 
                                      whiteSpace: 'pre-wrap',
                                      fontFamily: 'inherit',
                                      maxHeight: '300px',
                                      overflowY: 'auto'
                                    }}
                                  >
                                    {dropdownPreview[invoice.id].content}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="col-md-4">
                                <div className="card border">
                                  <div className="card-header py-2">
                                    <h6 className="mb-0">Email Details</h6>
                                  </div>
                                  <div className="card-body py-2">
                                    <div className="mb-2">
                                      <strong>Invoice:</strong><br />
                                      <small>{dropdownPreview[invoice.id].invoiceNumber}</small>
                                    </div>
                                    <div className="mb-2">
                                      <strong>Client:</strong><br />
                                      <small>{dropdownPreview[invoice.id].clientName}</small>
                                    </div>
                                    <div className="mb-2">
                                      <strong>To:</strong><br />
                                      <small>{dropdownPreview[invoice.id].to}</small>
                                    </div>
                                    {dropdownPreview[invoice.id].cc && (
                                      <div className="mb-2">
                                        <strong>CC:</strong><br />
                                        <small>{dropdownPreview[invoice.id].cc}</small>
                                      </div>
                                    )}
                                    {dropdownPreview[invoice.id].bcc && (
                                      <div className="mb-2">
                                        <strong>BCC:</strong><br />
                                        <small>{dropdownPreview[invoice.id].bcc}</small>
                                      </div>
                                    )}
                                    <div className="mb-0">
                                      <strong>Sent:</strong><br />
                                      <small>{formatDate(dropdownPreview[invoice.id].sentAt)}</small>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Email Preview Modal */}
      <EmailPreviewModal 
        invoiceId={selectedInvoiceId}
        isOpen={showEmailPreview}
        onClose={handleCloseEmailPreview}
      />
    </div>
  );
};

export default EmailPreviewPage;